import { requireFonts } from '../fonts/load';
import { fabric } from 'fabric';
import type { Doc } from '../editor/editor';
import { allPageJson, pageDocs, pageJson, withoutNotes } from '../editor/pageFormat';
import { checkPdfSize, pxToPt, rasterPlan, tilePlan } from './plan';
import { fitToBytes, JPG_Q, sizePlan, type JpgQuality, type PngQuality } from './options';

export type Format = 'png' | 'jpg' | 'pdf';
export interface ExportResult { blob: Blob; note?: string }

/** PNG/JPG choices from the export sheet (src/export/options.ts); left out = the page's own size, high quality */
export interface PictureOptions {
  mult: number;
  quality: JpgQuality | PngQuality;
  /** for quality 'limit' */
  limitBytes: number;
  /** PNG only: the page colour is not drawn */
  transparent: boolean;
}
const PLAIN: PictureOptions = { mult: 1, quality: 'high', limitBytes: 0, transparent: false };

/**
 * Builds an off-screen copy of the design from its saved JSON, so nothing from the editor
 * (selection handles, snap guides, grey workspace, zoom) can leak into the file. โน้ตแปะ are left out here: every
 * file, the print/document PDF and the previews are drawn through this.
 */
export async function offscreen(page: string, doc: Doc): Promise<fabric.StaticCanvas> {
  const json = withoutNotes(page);
  const el = document.createElement('canvas');
  const sc = new fabric.StaticCanvas(el, { enableRetinaScaling: false, renderOnAddRemove: false, backgroundColor: doc.bg });
  const objects = (JSON.parse(json).objects ?? []) as Array<Record<string, unknown>>;
  const expected = objects.length;
  // text is measured while loading: its fonts must be there first (they load when used, not at start-up)
  try { await requireFonts(objects); } catch (e) { sc.dispose(); throw e; }
  await new Promise<void>(res => sc.loadFromJSON(json, () => res()));
  sc.backgroundColor = doc.bg;
  const got = sc.getObjects().length;
  if (got < expected) {
    sc.dispose();
    throw new Error(`โหลดวัตถุในงานได้ ${got} จาก ${expected} ชิ้น (มักเป็นรูปที่เปิดไม่ได้) จึงไม่สร้างไฟล์ที่ขาดของ`);
  }
  return sc;
}

/** Fabric's object cache limits (a picture cropped to a circle/rounded shape always draws through its own cache) */
type CacheLimits = { perfLimitSizeTotal: number; maxCacheSideLimit: number };

/** Draw one region of the page (page px) at `scale` into a fresh canvas element. */
function drawRegion(sc: fabric.StaticCanvas, x: number, y: number, w: number, h: number, scale: number): HTMLCanvasElement {
  const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
  sc.setDimensions({ width: cw, height: ch });
  sc.setViewportTransform([scale, 0, 0, scale, -x * scale, -y * scale]);
  // a shaped picture's cache is capped at ~1448² by default and then stretched — blurry in the file; allow the
  // whole region while drawing (the same as the print PDF, src/print/pdf.ts drawRaster)
  const limits = fabric as unknown as CacheLimits;
  const saved = { total: limits.perfLimitSizeTotal, side: limits.maxCacheSideLimit };
  limits.perfLimitSizeTotal = Math.max(saved.total, cw * ch);
  limits.maxCacheSideLimit = Math.max(saved.side, cw, ch);
  sc.getObjects().forEach(o => { o.dirty = true; });
  try { sc.renderAll(); }
  finally { limits.perfLimitSizeTotal = saved.total; limits.maxCacheSideLimit = saved.side; }
  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  const ctx = out.getContext('2d');
  if (!ctx) throw new Error('เครื่องนี้สร้างภาพไม่ได้ (หน่วยความจำไม่พอ) ลองปิดแอปอื่นแล้วลองใหม่');
  ctx.drawImage(sc.getElement(), 0, 0);
  return out;
}

const toBlob = (c: HTMLCanvasElement, type: string, quality?: number) =>
  new Promise<Blob>((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('แปลงภาพเป็นไฟล์ไม่สำเร็จ (หน่วยความจำไม่พอ)')), type, quality));

/**
 * `json` is the whole design (src/editor/pageFormat.ts). PNG/JPG make one picture of page `page`;
 * "PDF ภาพ" has every page, one PDF page each. Each page is its own size (from the JSON); `base` gives the rest
 * (name, background) and page 1's size when the JSON has none.
 */
export async function exportDesign(
  json: string, base: Doc, format: Format, onProgress: (text: string) => void, page = 0, opt: PictureOptions = PLAIN
): Promise<ExportResult> {
  const docs = pageDocs(json, base);
  if (format === 'pdf') {
    for (const [k, d] of docs.entries()) {
      const err = checkPdfSize(d.w, d.h);
      if (err) throw new Error((docs.length > 1 ? `หน้า ${k + 1}: ` : '') + err);
    }
    return pictureOnlyPdf(json, docs, onProgress);
  }
  const doc = docs[page];
  if (!doc) throw new Error(`ไม่มีหน้า ${page + 1} ในงานนี้`);
  onProgress('กำลังเตรียมงาน…');
  const sc = await offscreen(pageJson(json, page), doc);
  try {
    const png = format === 'png', type = png ? 'image/png' : 'image/jpeg';
    if (png && opt.transparent) sc.backgroundColor = '';
    const plan = sizePlan(doc.w, doc.h, opt.mult);
    const notes: string[] = [];
    if (plan.reduced) notes.push(`ขอ ${plan.wantW} × ${plan.wantH} px แต่ได้ ${plan.outW} × ${plan.outH} px เพราะ iPad/iPhone วาดภาพใหญ่กว่านี้ไม่ได้ ถ้าต้องการความละเอียดเต็มให้ส่งออกเป็น PDF`);
    if (png && opt.transparent) notes.push('พื้นหลังโปร่งใส');
    const draw = async (k: number, q: number | null) => {
      const c = drawRegion(sc, 0, 0, doc.w, doc.h, plan.scale * k);
      try { return await toBlob(c, type, q ?? undefined); }
      finally { c.width = c.height = 0; }   // free memory early on phones (a size limit may draw several times)
    };
    if (opt.quality !== 'limit') {
      onProgress('กำลังวาดภาพ…');
      const blob = await draw(1, png ? null : JPG_Q[opt.quality as Exclude<JpgQuality, 'limit'>]);
      return { blob, note: notes.join(' · ') || undefined };
    }
    if (!(opt.limitBytes > 0)) throw new Error('ยังไม่ได้ใส่ขนาดไฟล์สูงสุด');
    onProgress('กำลังหาขนาดไฟล์ที่ไม่เกินที่กำหนด…');
    const r = await fitToBytes(draw, { limitBytes: opt.limitBytes, jpg: !png, longSide: Math.max(plan.outW, plan.outH) });
    const w = Math.max(1, Math.round(plan.outW * r.scale)), h = Math.max(1, Math.round(plan.outH * r.scale));
    notes.push(`จำกัดขนาดไฟล์: ได้ ${w} × ${h} px${r.quality != null ? ` คุณภาพ ${Math.round(r.quality * 100)}%` : ''}${r.scale < 1 ? ' (ย่อภาพลงเพื่อให้ไฟล์ไม่เกินที่กำหนด)' : ''}`);
    return { blob: r.blob, note: notes.join(' · ') };
  } finally {
    sc.dispose();
  }
}

/** "PDF ภาพ": every page as 300 DPI pictures (in tiles), one PDF page per page of the design, each at its own size. */
async function pictureOnlyPdf(json: string, docs: Doc[], onProgress: (text: string) => void): Promise<ExportResult> {
  const { jsPDF } = await import('jspdf');
  const sizeOf = (d: Doc) => {
    const wPt = pxToPt(d.w), hPt = pxToPt(d.h);
    return { wPt, hPt, orientation: wPt > hPt ? 'landscape' as const : 'portrait' as const };
  };
  const first = sizeOf(docs[0]);
  const pdf = new jsPDF({ unit: 'pt', format: [first.wPt, first.hPt], orientation: first.orientation, compress: true });
  const pages = allPageJson(json);
  for (let k = 0; k < pages.length; k++) {
    const doc = docs[k], size = sizeOf(doc), tiles = tilePlan(doc.w, doc.h);
    const where = pages.length > 1 ? `หน้า ${k + 1}: ` : '';
    onProgress(`${where}กำลังเตรียมงาน…`);
    if (k > 0) pdf.addPage([size.wPt, size.hPt], size.orientation);
    const sc = await offscreen(pages[k], doc).catch(e => { throw new Error(where + (e instanceof Error ? e.message : String(e))); });
    try {
      for (let i = 0; i < tiles.length; i++) {
        const t = tiles[i];
        onProgress(`${where}กำลังวาดส่วนที่ ${i + 1} จาก ${tiles.length}…`);
        const c = drawRegion(sc, t.x, t.y, t.w, t.h, 1);
        // JPEG at high quality: much smaller than PNG for photos, edges of text stay crisp at 300 DPI
        pdf.addImage(c.toDataURL('image/jpeg', 0.95), 'JPEG', pxToPt(t.x), pxToPt(t.y), pxToPt(t.w), pxToPt(t.h), undefined, 'NONE');
        c.width = c.height = 0; // free memory early on phones
        await new Promise(r => setTimeout(r, 0)); // let the progress text paint
      }
    } finally {
      sc.dispose();
    }
  }
  onProgress('กำลังรวมไฟล์ PDF…');
  return { blob: pdf.output('blob'), note: 'PDF แบบภาพ 300 DPI: ภาษาไทยถูกต้อง พิมพ์ได้ แต่แก้ตัวหนังสือในไฟล์ไม่ได้' };
}

/**
 * "ดาวน์โหลดที่เลือก" (object menu): only these objects, on a see-through background, cut to `box` (page px) —
 * full size, made smaller only where an iPad cannot draw that many pixels (as PNG export).
 */
export async function exportObjectsPng(objects: Array<Record<string, unknown>>, box: { x: number; y: number; w: number; h: number })
  : Promise<ExportResult> {
  const json = JSON.stringify({ version: '5.3.0', objects });
  const sc = await offscreen(json, { w: box.w, h: box.h, name: '', bg: '' });
  try {
    sc.backgroundColor = '';
    const plan = rasterPlan(box.w, box.h);
    const c = drawRegion(sc, box.x, box.y, box.w, box.h, plan.scale);
    const blob = await toBlob(c, 'image/png');
    c.width = c.height = 0;
    return { blob, note: plan.reduced ? `ลดขนาดเหลือ ${plan.outW} × ${plan.outH} px (${Math.round(plan.scale * 100)}%) เพราะ iPad/iPhone วาดภาพใหญ่กว่านี้ไม่ได้` : undefined };
  } finally {
    sc.dispose();
  }
}

/** Small preview picture (longest side `maxSide` px, JPEG data URL) for template galleries and the page list.
 * A whole multi-page design shows its first page (Fabric loads the top-level objects = page 1). */
export async function renderThumbnail(json: string, doc: Doc, maxSide = 480): Promise<string> {
  const sc = await offscreen(json, doc);
  try {
    const scale = Math.min(1, maxSide / Math.max(doc.w, doc.h));
    const c = drawRegion(sc, 0, 0, doc.w, doc.h, scale);
    const url = c.toDataURL('image/jpeg', 0.85);
    c.width = c.height = 0;
    if (!url.startsWith('data:image/jpeg')) throw new Error('เครื่องนี้สร้างรูปตัวอย่างไม่ได้');
    return url;
  } finally {
    sc.dispose();
  }
}
